import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { isAdmin } from '../utils/admin';

const Posts = ({ user }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({ title: '', content: '', image_url: '' });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await api.get('/posts');
      setPosts(response.data);
    } catch (err) {
      console.error('Failed to load posts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!user) {
      alert('Please login to create a post');
      return;
    }

    try {
      await api.post('/posts', formData);
      setShowCreateForm(false);
      setFormData({ title: '', content: '', image_url: '' });
      fetchPosts();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create post');
    }
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleString(undefined, { timeZone: 'America/Los_Angeles', dateStyle: 'short', timeStyle: 'short' });

  const uploadImage = async (file) => {
    const formDataUpload = new FormData();
    formDataUpload.append('image', file);
    const res = await api.post('/uploads', formDataUpload, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data.url;
  };

  const handleDeletePost = async (e, postId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Delete this post?')) return;
    try {
      await api.delete(`/posts/${postId}`);
      fetchPosts();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete post');
    }
  };

  const handleImageFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setFormData(prev => ({ ...prev, image_url: url }));
    } catch (err) {
      alert(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  if (loading) {
    return <div>Loading posts...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Posts</h1>
        {user && isAdmin(user) && (
          <button className="btn btn-primary" onClick={() => setShowCreateForm(!showCreateForm)}>
            {showCreateForm ? 'Cancel' : 'Create Post'}
          </button>
        )}
      </div>

      {showCreateForm && user && isAdmin(user) && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <h2>Create New Post</h2>
          <form onSubmit={handleCreatePost}>
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Content *</label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Image (optional)</label>
              <div style={{ marginBottom: 8 }}>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleImageFile}
                  disabled={uploading}
                />
                {uploading && <span style={{ marginLeft: 8, color: '#666' }}>Uploading…</span>}
              </div>
              <input
                type="text"
                placeholder="Or paste image URL"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                style={{ width: '100%' }}
              />
              {formData.image_url && (
                <div style={{ marginTop: 8 }}>
                  <img src={formData.image_url.startsWith('/') ? window.location.origin + formData.image_url : formData.image_url} alt="Preview" style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }} />
                </div>
              )}
            </div>
            <button type="submit" className="btn btn-primary">Create Post</button>
          </form>
        </div>
      )}

      {posts.length === 0 ? (
        <div className="card">
          <p>No posts yet.{isAdmin(user) ? ' Create a post to get started.' : ''}</p>
        </div>
      ) : (
        posts.map((post) => (
          <div key={post.id ?? post.title} className="card">
            <Link to={post.id != null ? `/posts/${post.id}` : '#'} style={{ textDecoration: 'none', color: 'inherit' }} onClick={(e) => { if (post.id == null) e.preventDefault(); }}>
              <h3>{post.title}</h3>
              {post.image_url && (
                <img
                  src={post.image_url.startsWith('/') ? window.location.origin + post.image_url : post.image_url}
                  alt=""
                  style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain', marginBottom: 10 }}
                />
              )}
              <p style={{ marginBottom: '10px', whiteSpace: 'pre-wrap' }}>
                {post.content.length > 200 ? post.content.substring(0, 200) + '...' : post.content}
              </p>
            </Link>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '15px', fontSize: '14px', color: '#666' }}>
              <span>By {post.author_name} • {formatDate(post.created_at)}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>{post.comment_count || 0} comment(s)</span>
                {user && isAdmin(user) && (
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={(e) => handleDeletePost(e, post.id)}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default Posts;

